import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Download,
  Trash2,
  FolderInput,
  Eye,
  Star,
  MoreHorizontal,
  PenLine,
  EyeOff,
} from 'lucide-react';
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { cn } from '@/core/lib/utils';
import { getColorValue } from '@/core/lib/color-contrast';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { formatTimeAgo } from '@/shared/utils/formatTimeAgo';
import { getFileTypeConfig } from '../fileTypeConfig';
import type { ProjectFileDto, ProjectFolderNodeDto } from '../types';

interface ProjectFileCardProps {
  file: ProjectFileDto;
  folders: ProjectFolderNodeDto[];
  isSelected: boolean;
  onDelete: (fileId: string) => void;
  onMove: (fileId: string) => void;
  onRename: (fileId: string, currentName: string) => void;
  onToggleFavorite: (fileId: string, isFavorite: boolean) => void;
  onToggleVisibility: (fileId: string, isPrivate: boolean) => void;
  onSelect: (id: string, e: React.MouseEvent) => void;
  onPreview: (file: ProjectFileDto) => void;
}

export function ProjectFileCard({
  file,
  isSelected,
  onDelete,
  onMove,
  onRename,
  onToggleFavorite,
  onToggleVisibility,
  onSelect,
  onPreview,
}: ProjectFileCardProps) {
  const { t, i18n } = useTranslation('projectFiles');
  const [isDragging, setIsDragging] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const typeConfig = getFileTypeConfig(file.mimeType);
  const colorValue = getColorValue(typeConfig.token);
  const TypeIcon = typeConfig.icon;

  useEffect(() => {
    if (!ref.current || !file.id) return;
    return draggable({
      element: ref.current,
      getInitialData: () => ({ type: 'file', id: file.id }),
      onDragStart: () => setIsDragging(true),
      onDrop: () => setIsDragging(false),
    });
  }, [file.id]);

  function handleDownload() {
    if (!file.downloadUrl || !file.fileName) return;
    const anchor = document.createElement('a');
    anchor.href = file.downloadUrl;
    anchor.download = file.fileName;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.click();
  }

  return (
    <div
      ref={ref}
      className={cn(
        'group/card relative flex flex-col overflow-hidden rounded-lg border bg-card shadow-sm transition-all cursor-grab active:cursor-grabbing',
        'hover:shadow-md',
        isSelected && 'ring-2 ring-primary',
        isDragging && 'opacity-50',
      )}
      data-testid="project-files-card"
      onClick={(e) => file.id && onSelect(file.id, e)}
      onDoubleClick={() => onPreview(file)}
    >
      {/* Top color stripe */}
      <div className="h-1 shrink-0" style={{ backgroundColor: colorValue }} />

      <div className="flex flex-1 flex-col gap-2 p-3">
        {/* Type badge */}
        <div>
          <span
            className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-xs font-medium"
            style={{
              backgroundColor: `color-mix(in oklch, ${colorValue} 15%, transparent)`,
              borderColor: `color-mix(in oklch, ${colorValue} 40%, transparent)`,
              color: colorValue,
            }}
          >
            <TypeIcon className="size-3 shrink-0" />
            {t(typeConfig.typeKey)}
          </span>
        </div>

        {/* File name */}
        <p
          className="line-clamp-2 flex-1 text-sm font-medium leading-snug"
          title={file.fileName ?? undefined}
        >
          {file.fileName}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          {file.uploadedBy ? (
            <span className="max-w-[55%] truncate">{file.uploadedBy}</span>
          ) : (
            <span />
          )}
          <span className="shrink-0">{formatTimeAgo(file.createdAt, i18n.language)}</span>
        </div>

        {/* Hover action bar */}
        <div
          className="flex items-center gap-1 border-t pt-2 opacity-0 transition-opacity group-hover/card:opacity-100 focus-within:opacity-100"
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                onClick={() => onPreview(file)}
                data-testid="project-files-card-preview"
                aria-label={t('item.preview')}
              >
                <Eye className="size-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('item.preview')}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-7"
                onClick={handleDownload}
                disabled={!file.downloadUrl}
                data-testid="project-files-card-download"
                aria-label={t('item.download')}
              >
                <Download className="size-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('item.download')}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={cn('size-7', file.isFavorite && 'text-amber-500')}
                onClick={() => file.id && onToggleFavorite(file.id, !file.isFavorite)}
                data-testid="project-files-card-favorite"
                aria-label={file.isFavorite ? t('item.unfavorite') : t('item.favorite')}
              >
                <Star className={cn('size-3.5', file.isFavorite && 'fill-current')} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {file.isFavorite ? t('item.unfavorite') : t('item.favorite')}
            </TooltipContent>
          </Tooltip>

          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7 ml-auto"
                    data-testid="project-files-card-actions"
                    aria-label={t('item.actions')}
                  >
                    <MoreHorizontal className="size-3.5" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>{t('item.actions')}</TooltipContent>
            </Tooltip>

            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={() => file.id && onRename(file.id, file.fileName ?? '')}
                data-testid="project-files-card-rename"
              >
                <PenLine className="mr-2 size-4" />
                {t('item.rename')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => file.id && onMove(file.id)}
                data-testid="project-files-card-move"
              >
                <FolderInput className="mr-2 size-4" />
                {t('item.move')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => file.id && onToggleVisibility(file.id, !file.isPrivate)}
                data-testid="project-files-card-visibility"
              >
                <EyeOff className="mr-2 size-4" />
                {file.isPrivate ? t('item.makePublic') : t('item.makePrivate')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => file.id && onDelete(file.id)}
                data-testid="project-files-card-delete"
              >
                <Trash2 className="mr-2 size-4" />
                {t('item.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
